import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, limit } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import ProductCard from '../components/ProductCard';
import SpaceHero from '../components/SpaceHero';

function Home() {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadFeatured() {
      try {
        const q = query(collection(db, 'products'), limit(4));
        const snapshot = await getDocs(q);
        const products = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setFeaturedProducts(products);
      } catch (err) {
        console.error('Error loading featured products:', err);
      }
      setLoading(false);
    }
    loadFeatured();
  }, []);

  // Intersection Observer — animates in on scroll down, resets when scrolled back up
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          } else {
            entry.target.classList.remove('visible'); // reset so it re-animates on next scroll
          }
        });
      },
      { threshold: 0.12 }
    );
    document.querySelectorAll('.scroll-reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [featuredProducts]);

  return (
    <div>
      {/* ---- Hero ---- */}
      <SpaceHero />

      {/* ---- Featured Products ---- */}
      <div className="container py-5">
        <div className="d-flex justify-content-between align-items-baseline mb-4 scroll-reveal">
          <h5
            className="fw-bold mb-0 text-uppercase"
            style={{ letterSpacing: '0.1em' }}
          >
            Featured
          </h5>
          <Link
            to="/products"
            className="small text-decoration-none"
            style={{ color: '#888' }}
          >
            View all →
          </Link>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div
              className="spinner-border spinner-border-sm"
              style={{ color: '#c84b11' }}
              role="status"
            >
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : featuredProducts.length > 0 ? (
          <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-4 g-3 scroll-stagger">
            {featuredProducts.map((product, index) => (
              <div
                className="col scroll-reveal"
                key={product.id}
                style={{ transitionDelay: `${index * 90}ms` }}
              >
                <ProductCard product={product} />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-5 scroll-reveal" style={{ color: '#aaa' }}>
            <p className="mb-0">No products yet — check back soon.</p>
          </div>
        )}
      </div>

      {/* ---- Brand pillars ---- */}
      <div style={{ background: '#f4f4f4', borderTop: '1px solid #e0e0e0' }}>
        <div className="container py-5">
          <div className="row g-4 text-center">
            <div className="col-md-4 scroll-reveal" style={{ transitionDelay: '0ms' }}>
              <p className="fw-bold mb-1 text-uppercase" style={{ letterSpacing: '0.08em', fontSize: '0.85rem' }}>
                Quality First
              </p>
              <p className="small mb-0" style={{ color: '#888' }}>
                Every product is built to last. No cheap stuff, ever.
              </p>
            </div>
            <div className="col-md-4 scroll-reveal" style={{ transitionDelay: '100ms' }}>
              <p className="fw-bold mb-1 text-uppercase" style={{ letterSpacing: '0.08em', fontSize: '0.85rem' }}>
                Fast Shipping
              </p>
              <p className="small mb-0" style={{ color: '#888' }}>
                Orders ship fast. Your gear shouldn't make you wait.
              </p>
            </div>
            <div className="col-md-4 scroll-reveal" style={{ transitionDelay: '200ms' }}>
              <p className="fw-bold mb-1 text-uppercase" style={{ letterSpacing: '0.08em', fontSize: '0.85rem' }}>
                Original Designs
              </p>
              <p className="small mb-0" style={{ color: '#888' }}>
                Everything we make is designed in-house. You won't find it anywhere else.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Home;
